import { useState, useEffect } from 'react';
import { Copy, ArrowDownCircle, ArrowUpCircle, RefreshCw, Info, CreditCard,
         Wallet as WalletIcon, History, Send, QrCode, Zap, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const COIN_COLORS   = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };
const COIN_NETWORKS = { BTC:'Bitcoin Network', ETH:'Ethereum (ERC-20)', BNB:'BNB Smart Chain (BEP-20)', SOL:'Solana Network', USDT:'ERC-20 or BEP-20', SUI:'Sui Network', ASTER:'Ethereum (ERC-20)', HYPE:'HyperLiquid L1', XRP:'XRP Ledger (XRPL)' };
const COINS = ['USDT','BNB','ETH','SOL','BTC','SUI','ASTER','HYPE','XRP'];

const TRANSAK_ENV     = 'STAGING';
const TRANSAK_API_KEY = 'your_transak_api_key';

// Testnet faucet amounts per coin
const FAUCET_AMOUNTS = { USDT:100, BNB:0.5, ETH:0.05, SOL:2, BTC:0.001, SUI:10, ASTER:50, HYPE:2, XRP:50 };

export default function Wallet() {
  const { user } = useAuth();
  const [tab, setTab]               = useState('balance');
  const [balances, setBalances]     = useState({});
  const [totalUSD, setTotalUSD]     = useState(0);
  const [txs, setTxs]               = useState([]);
  const [prices, setPrices]         = useState({});
  const [addresses, setAddresses]   = useState({});
  const [loading, setLoading]       = useState(true);
  const [txLoading, setTxLoading]   = useState(true);

  // Deposit
  const [selectedCoin, setSelectedCoin]   = useState('USDT');
  const [verifyForm, setVerifyForm]       = useState({ txHash:'', amount:'' });
  const [verifying, setVerifying]         = useState(false);

  // Receive
  const [receiveCoin, setReceiveCoin]     = useState('USDT');
  const [faucetLoading, setFaucetLoading] = useState('');
  const [copiedAddr, setCopiedAddr]       = useState(false);

  // Withdraw
  const [withdrawForm, setWithdrawForm]   = useState({ toAddress:'', currency:'USDT', amount:'' });
  const [withdrawing, setWithdrawing]     = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [bal, addr, pr, tx] = await Promise.all([
        api.get('/wallet/balance'),
        api.get('/wallet/addresses'),
        api.get('/prices/live'),
        api.get('/wallet/transactions'),
      ]);
      setBalances(bal.data.balance || {});
      setTotalUSD(bal.data.totalUSD || 0);
      setAddresses(addr.data.addresses || {});
      setPrices(pr.data.prices || {});
      setTxs(tx.data.transactions || []);
    } catch {}
    finally { setLoading(false); setTxLoading(false); }
  };

  const openTransak = (defaultCurrency = 'USDT') => {
    const walletAddress = getAddress(defaultCurrency) || '';
    const params = new URLSearchParams({
      apiKey: TRANSAK_API_KEY,
      environment: TRANSAK_ENV,
      cryptoCurrencyCode: defaultCurrency,
      walletAddress,
      themeColor: 'FF6B00',
      hideMenu: 'true',
      defaultFiatCurrency: 'USD',
    });
    const url = `https://global-stg.transak.com/?${params}`;
    const popup = window.open(url, 'transak', 'width=450,height=700,scrollbars=yes');
    if (!popup) toast.error('Popup blocked. Please allow popups and try again.');
  };

  const verifyDeposit = async () => {
    if (!verifyForm.txHash || !verifyForm.amount) return toast.error('Enter tx hash and amount');
    setVerifying(true);
    try {
      const res = await api.post('/wallet/verify-deposit', {
        txHash: verifyForm.txHash.trim(), currency: selectedCoin, amount: verifyForm.amount,
      });
      toast.success(res.data.message);
      setVerifyForm({ txHash:'', amount:'' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Verification failed'); }
    finally { setVerifying(false); }
  };

  // ── Testnet faucet — mint test tokens directly to this wallet ────────────
  const claimFaucet = async (coin) => {
    setFaucetLoading(coin);
    await new Promise(r => setTimeout(r, 1200));
    try {
      const amt   = FAUCET_AMOUNTS[coin] || 1;
      const txHash = '0xFAUCET_' + coin + '_' + Date.now().toString(16).toUpperCase();
      const res = await api.post('/wallet/verify-deposit', { txHash, currency: coin, amount: amt });
      toast.success(`🚰 ${amt} ${coin} testnet tokens sent to your wallet!`);
      fetchAll();
    } catch (err) { toast.error('Faucet error — try again'); }
    finally { setFaucetLoading(''); }
  };

  const submitWithdraw = async () => {
    if (!withdrawForm.toAddress || !withdrawForm.amount) return toast.error('Fill all fields');
    setWithdrawing(true);
    try {
      const res = await api.post('/wallet/withdraw', withdrawForm);
      toast.success(res.data.message);
      setWithdrawForm({ toAddress:'', currency:'USDT', amount:'' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || 'Withdrawal failed'); }
    finally { setWithdrawing(false); }
  };

  const getAddress = (coin) => {
    if (coin === 'BTC') return addresses.BTC;
    if (coin === 'SOL') return addresses.SOL;
    if (coin === 'SUI') return addresses.SUI;
    if (coin === 'XRP') return addresses.XRP;
    return addresses.EVM;
  };

  const copy = (text, label = '') => {
    if (!text) return toast.error('Address not available');
    navigator.clipboard.writeText(text);
    setCopiedAddr(true);
    setTimeout(() => setCopiedAddr(false), 2000);
    toast.success(label ? `${label} copied!` : 'Copied!');
  };

  const TABS = [
    { id:'balance',  icon: WalletIcon,      label:'Balance'  },
    { id:'receive',  icon: QrCode,          label:'Receive'  },
    { id:'deposit',  icon: ArrowDownCircle, label:'Deposit'  },
    { id:'withdraw', icon: ArrowUpCircle,   label:'Withdraw' },
    { id:'history',  icon: History,         label:'History'  },
  ];

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-1 mt-4" style={{ fontFamily:'Sora' }}>My Wallet</h1>
      <p className="text-brand-muted text-sm mb-5">Receive · Deposit · Pay · Withdraw</p>

      {/* Tab nav — scrollable on small screens */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl overflow-x-auto" style={{ background:'#12121A', border:'1px solid #1E1E2E', scrollbarWidth:'none' }}>
        {TABS.map(({ id, icon:Icon, label }) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 flex-shrink-0 flex flex-col items-center py-2 gap-0.5 rounded-lg text-xs font-semibold transition-all min-w-[56px]"
            style={{ background:tab===id?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color:tab===id?'white':'#6B6B8A' }}>
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* ── BALANCE ── */}
      {tab === 'balance' && (
        <div className="space-y-4">
          <div className="rounded-2xl p-5" style={{ background:'linear-gradient(135deg,#0A0A0F,#12121A)', border:'1px solid rgba(255,107,0,0.3)' }}>
            <p className="text-brand-muted text-sm mb-1">Total Balance</p>
            <p className="text-4xl font-bold gradient-text mb-1" style={{ fontFamily:'Sora' }}>${totalUSD.toFixed(2)}</p>
            <p className="text-xs text-brand-muted">USD equivalent across all coins</p>
          </div>

          <div className="space-y-2">
            {loading ? (
              Array.from({length:4}).map((_,i) => <div key={i} className="h-14 rounded-xl animate-pulse" style={{ background:'#12121A' }} />)
            ) : Object.entries(balances).filter(([,v]) => v > 0).length === 0 ? (
              <div className="text-center py-10 text-brand-muted">
                <WalletIcon size={40} className="mx-auto mb-3 opacity-30" />
                <p className="mb-2">No balance yet</p>
                <p className="text-xs mb-4">Use the Receive tab to get testnet tokens</p>
                <button onClick={() => setTab('receive')} className="btn-primary px-8 text-sm">Get Testnet Tokens</button>
              </div>
            ) : (
              Object.entries(balances).map(([coin, bal]) => {
                if (bal <= 0) return null;
                const color = COIN_COLORS[coin] || '#FF6B00';
                return (
                  <div key={coin} className="flex items-center justify-between p-4 rounded-2xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs" style={{ background:`${color}20`, color, fontFamily:'JetBrains Mono' }}>{coin}</div>
                      <div>
                        <p className="font-bold font-mono" style={{ color }}>{bal.toFixed(coin==='BTC'?8:4)}</p>
                        <p className="text-xs text-brand-muted">{COIN_NETWORKS[coin]}</p>
                      </div>
                    </div>
                    <p className="font-semibold text-sm">${(bal*(prices[coin]||0)).toFixed(2)}</p>
                  </div>
                );
              })
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => setTab('receive')} className="btn-primary py-3 flex items-center justify-center gap-1 text-sm">
              <QrCode size={14} /> Receive
            </button>
            <button onClick={() => setTab('deposit')} className="btn-outline py-3 flex items-center justify-center gap-1 text-sm">
              <ArrowDownCircle size={14} /> Deposit
            </button>
            <button onClick={() => setTab('withdraw')} className="btn-outline py-3 flex items-center justify-center gap-1 text-sm">
              <ArrowUpCircle size={14} /> Send
            </button>
          </div>
        </div>
      )}

      {/* ── RECEIVE ── */}
      {tab === 'receive' && (
        <div className="space-y-4">

          {/* Testnet faucet — prominent */}
          <div className="rounded-2xl p-4" style={{ background:'linear-gradient(135deg,rgba(0,200,100,0.08),rgba(77,162,255,0.06))', border:'1px solid rgba(0,200,100,0.3)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Zap size={18} className="text-green-400" />
              <h2 className="font-bold" style={{ fontFamily:'Sora', color:'#00C864' }}>🚰 Testnet Faucet</h2>
              <span className="badge text-xs" style={{ background:'rgba(0,200,100,0.15)', color:'#00C864', border:'1px solid rgba(0,200,100,0.3)' }}>Free Tokens</span>
            </div>
            <p className="text-xs text-brand-muted mb-4">
              This is a testnet. Tap any coin below to instantly receive free test tokens in your wallet — no real money needed.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {COINS.map(coin => (
                <button key={coin} onClick={() => claimFaucet(coin)} disabled={!!faucetLoading}
                  className="p-3 rounded-xl text-center transition-all hover:scale-105 disabled:opacity-50"
                  style={{ background:'#12121A', border:`1px solid ${COIN_COLORS[coin]}40` }}>
                  {faucetLoading === coin ? (
                    <div className="spinner mx-auto mb-1" />
                  ) : (
                    <p className="font-bold text-sm mb-1" style={{ color:COIN_COLORS[coin], fontFamily:'JetBrains Mono' }}>{coin}</p>
                  )}
                  <p className="text-xs text-brand-muted">+{FAUCET_AMOUNTS[coin]}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Receive from external wallet */}
          <div className="glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <QrCode size={16} className="text-brand-orange" />
              <h2 className="font-bold text-sm" style={{ fontFamily:'Sora' }}>Receive from External Wallet</h2>
            </div>
            <p className="text-xs text-brand-muted mb-3">Share your wallet address to receive crypto from MetaMask, Phantom, Trust Wallet, Binance, etc.</p>

            {/* Coin selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {COINS.map(c => (
                <button key={c} onClick={() => setReceiveCoin(c)}
                  className="px-2.5 py-1 rounded-lg text-xs font-bold transition-all"
                  style={{ background:receiveCoin===c?`${COIN_COLORS[c]}20`:'#0A0A0F', color:receiveCoin===c?COIN_COLORS[c]:'#6B6B8A', border:`1px solid ${receiveCoin===c?COIN_COLORS[c]:'#1E1E2E'}`, fontFamily:'JetBrains Mono' }}>
                  {c}
                </button>
              ))}
            </div>

            {/* Address display */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-brand-muted">{receiveCoin} address · {COIN_NETWORKS[receiveCoin]}</p>
              </div>

              {/* Big address box */}
              <div className="p-4 rounded-2xl mb-2" style={{ background:'#0A0A0F', border:`2px solid ${COIN_COLORS[receiveCoin]}60` }}>
                <p className="font-mono text-sm break-all select-all text-center leading-relaxed" style={{ color:'#E0E0F0' }}>
                  {getAddress(receiveCoin) || 'Generating address...'}
                </p>
              </div>

              <button onClick={() => copy(getAddress(receiveCoin), `${receiveCoin} address`)}
                className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
                style={{ background: copiedAddr ? 'rgba(0,200,100,0.15)' : `${COIN_COLORS[receiveCoin]}20`, color: copiedAddr ? '#00C864' : COIN_COLORS[receiveCoin], border:`1px solid ${copiedAddr?'rgba(0,200,100,0.4)':COIN_COLORS[receiveCoin]+'40'}` }}>
                {copiedAddr ? <><CheckCircle size={16} /> Copied!</> : <><Copy size={16} /> Copy {receiveCoin} Address</>}
              </button>

              <p className="text-xs text-red-400 mt-2 text-center">
                ⚠️ Only send {receiveCoin} on {COIN_NETWORKS[receiveCoin]} to this address
              </p>
            </div>

            {/* All addresses quick view */}
            <div className="mt-4 p-3 rounded-xl" style={{ background:'#0A0A0F', border:'1px solid #1E1E2E' }}>
              <p className="text-xs font-semibold mb-2 text-brand-muted">All Your Addresses</p>
              <div className="space-y-2">
                {[
                  { label:'EVM (ETH/BNB/USDT/MATIC)', key:'EVM', coins:'ETH · BNB · USDT · ASTER · HYPE' },
                  { label:'Solana', key:'SOL', coins:'SOL' },
                  { label:'Bitcoin', key:'BTC', coins:'BTC' },
                  { label:'XRP Ledger', key:'XRP', coins:'XRP' },
                  { label:'Sui Network', key:'SUI', coins:'SUI' },
                ].map(({ label, key, coins }) => (
                  <div key={key} className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-brand-muted">{label}</p>
                      <p className="font-mono text-xs break-all" style={{ color:'#E0E0F0' }}>{addresses[key] || '—'}</p>
                      <p className="text-xs" style={{ color:'#4DA2FF' }}>{coins}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(addresses[key]||''); toast.success(`${key} address copied!`); }} className="flex-shrink-0 mt-1">
                      <Copy size={12} className="text-brand-orange" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Already sent — verify to credit */}
          <div className="glass rounded-2xl p-4">
            <p className="text-xs font-semibold mb-1">Already sent from external wallet?</p>
            <p className="text-xs text-brand-muted mb-3">Paste your transaction hash to credit your balance instantly.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {COINS.map(c => (
                <button key={c} onClick={() => setSelectedCoin(c)}
                  className="px-2.5 py-1 rounded-lg text-xs font-bold"
                  style={{ background:selectedCoin===c?`${COIN_COLORS[c]}20`:'#0A0A0F', color:selectedCoin===c?COIN_COLORS[c]:'#6B6B8A', border:`1px solid ${selectedCoin===c?COIN_COLORS[c]:'#1E1E2E'}`, fontFamily:'JetBrains Mono' }}>
                  {c}
                </button>
              ))}
            </div>
            <input className="input-dark text-xs mb-2 font-mono" placeholder="Paste transaction hash (txHash)"
              value={verifyForm.txHash} onChange={e => setVerifyForm({...verifyForm, txHash:e.target.value})} />
            <div className="flex gap-2">
              <input className="input-dark text-xs flex-1" placeholder={`Amount (${selectedCoin})`} type="number"
                value={verifyForm.amount} onChange={e => setVerifyForm({...verifyForm, amount:e.target.value})} />
              <button onClick={verifyDeposit} disabled={verifying} className="btn-primary px-4 text-xs flex items-center gap-1">
                {verifying ? <div className="spinner" /> : <><RefreshCw size={12} /> Verify</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DEPOSIT ── */}
      {tab === 'deposit' && (
        <div className="space-y-4">
          <div className="rounded-2xl overflow-hidden" style={{ border:'1px solid rgba(255,107,0,0.4)' }}>
            <div className="p-4" style={{ background:'rgba(255,107,0,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <CreditCard size={18} className="text-brand-orange" />
                <h2 className="font-bold" style={{ fontFamily:'Sora' }}>Bank / Card Deposit</h2>
                <span className="badge badge-orange text-xs ml-1">Recommended</span>
              </div>
              <p className="text-xs text-brand-muted mb-4">
                Pay with your bank account, debit card or ATM card. Powered by Transak — supports Naira, Cedis, Shillings, USD, EUR and 170+ countries.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { coin:'USDT', label:'Deposit USDT', sub:'Most stable — recommended' },
                  { coin:'BNB',  label:'Deposit BNB',  sub:'Fast & low fees' },
                  { coin:'ETH',  label:'Deposit ETH',  sub:'Ethereum network' },
                  { coin:'SOL',  label:'Deposit SOL',  sub:'Solana network' },
                ].map(({ coin, label, sub }) => (
                  <button key={coin} onClick={() => openTransak(coin)}
                    className="p-3 rounded-xl text-left transition-all hover:scale-105"
                    style={{ background:'#12121A', border:`1px solid ${COIN_COLORS[coin]}40` }}>
                    <p className="font-bold text-sm" style={{ color:COIN_COLORS[coin], fontFamily:'JetBrains Mono' }}>{coin}</p>
                    <p className="text-xs font-semibold text-brand-text">{label}</p>
                    <p className="text-xs text-brand-muted">{sub}</p>
                  </button>
                ))}
              </div>
              <p className="text-xs text-brand-muted mt-3 flex items-center gap-1">
                <Info size={11} /> Crypto arrives automatically after payment confirmation
              </p>
            </div>
          </div>

          {/* Testnet shortcut */}
          <div className="p-3 rounded-xl flex items-center gap-3" style={{ background:'rgba(0,200,100,0.05)', border:'1px solid rgba(0,200,100,0.2)' }}>
            <Zap size={16} className="text-green-400 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-green-300">This is a testnet</p>
              <p className="text-xs text-brand-muted">Use the <button onClick={() => setTab('receive')} className="text-green-400 underline">Receive tab → Testnet Faucet</button> to get free test tokens instantly.</p>
            </div>
          </div>

          {/* Live prices */}
          <div className="glass rounded-2xl p-4">
            <h3 className="font-semibold text-sm mb-3" style={{ fontFamily:'Sora' }}>Live Prices</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(prices).slice(0,8).map(([coin, price]) => (
                <div key={coin} className="flex justify-between text-xs p-2 rounded-lg" style={{ background:'#0A0A0F' }}>
                  <span className="font-bold font-mono" style={{ color:COIN_COLORS[coin]||'#FF6B00' }}>{coin}</span>
                  <span className="text-brand-muted">${Number(price).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── WITHDRAW ── */}
      {tab === 'withdraw' && (
        <div className="space-y-4">
          <div className="glass rounded-2xl p-4">
            <h2 className="font-bold mb-3" style={{ fontFamily:'Sora' }}>Send / Withdraw</h2>
            <p className="text-xs text-brand-muted mb-4">Send crypto from your CryptoMart wallet to any external wallet address.</p>

            <div className="space-y-3">
              <div>
                <p className="text-xs text-brand-muted mb-1.5">Select coin</p>
                <div className="flex flex-wrap gap-2">
                  {COINS.map(c => {
                    const bal = balances[c] || 0;
                    return (
                      <button key={c} onClick={() => setWithdrawForm({...withdrawForm, currency:c})}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                        style={{ background:withdrawForm.currency===c?`${COIN_COLORS[c]}20`:'#0A0A0F', color:withdrawForm.currency===c?COIN_COLORS[c]:'#6B6B8A', border:`1px solid ${withdrawForm.currency===c?COIN_COLORS[c]:'#1E1E2E'}`, fontFamily:'JetBrains Mono', opacity: bal > 0 ? 1 : 0.4 }}>
                        {c} {bal > 0 && <span className="ml-1 opacity-60">{bal.toFixed(4)}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-brand-muted">Amount</p>
                  <button onClick={() => setWithdrawForm({...withdrawForm, amount:(balances[withdrawForm.currency]||0).toString()})}
                    className="text-xs text-brand-orange hover:underline">
                    Max: {(balances[withdrawForm.currency]||0).toFixed(6)}
                  </button>
                </div>
                <input className="input-dark" type="number" placeholder={`Amount in ${withdrawForm.currency}`}
                  value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount:e.target.value})} />
              </div>

              <div>
                <p className="text-xs text-brand-muted mb-1.5">Destination address</p>
                <input className="input-dark font-mono text-sm" placeholder="Paste wallet address"
                  value={withdrawForm.toAddress} onChange={e => setWithdrawForm({...withdrawForm, toAddress:e.target.value.trim()})} />
                <p className="text-xs text-red-400 mt-1">⚠️ Double-check the address. Testnet — no real funds at risk.</p>
              </div>

              <button onClick={submitWithdraw} disabled={withdrawing || !withdrawForm.toAddress || !withdrawForm.amount}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-40">
                {withdrawing ? <div className="spinner" /> : <><Send size={15} /> Send {withdrawForm.currency}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HISTORY ── */}
      {tab === 'history' && (
        <div>
          {txLoading ? (
            <div className="flex justify-center py-16"><div className="spinner" /></div>
          ) : txs.length === 0 ? (
            <div className="text-center py-16 text-brand-muted">
              <History size={40} className="mx-auto mb-3 opacity-30" />
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {txs.map(tx => {
                const isCredit = ['deposit','escrow_release','refund'].includes(tx.type);
                const color = COIN_COLORS[tx.currency] || '#FF6B00';
                const typeLabels = { deposit:'Deposit', withdrawal:'Withdrawal', payment:'Payment', escrow_lock:'Escrow Locked', escrow_release:'Payout Received', refund:'Refund' };
                return (
                  <div key={tx._id} className="flex items-center gap-3 p-3 rounded-xl" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: isCredit?'rgba(0,200,100,0.1)':'rgba(255,50,50,0.1)' }}>
                      {isCredit ? <ArrowDownCircle size={16} className="text-green-400" /> : <ArrowUpCircle size={16} className="text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{typeLabels[tx.type] || tx.type}</p>
                      <p className="text-xs text-brand-muted truncate font-mono">{tx.txHash || tx.note || '—'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold font-mono text-sm" style={{ color: isCredit?'#00C864':color }}>
                        {isCredit?'+':'-'}{tx.amount.toFixed(6)} {tx.currency}
                      </p>
                      <p className="text-xs" style={{ color: tx.status==='confirmed'?'#00C864':tx.status==='pending'?'#F5A623':'#FF3232' }}>{tx.status}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
